import { tween } from '../motion-choreography.js';
import { getLoaderGeometry, buildPath, lerpPoints } from '../loader-shapes.js';

// Each morph leg rides the most expressive fragment the design system offers —
// the largest duration (extra-long, 560ms) on the branded "holds back, then
// surges" curve (emphasized). This "extra-long emphasized" fragment resolves from
// the Tier 1 CSS motion tokens at run time (see resolveMotion in
// motion-choreography.js), so the loader stays in lockstep with the design
// system, including the reduced-motion collapse.
const MOTION = 'extra-long emphasized';

// The `color` variant crossfades the mark through the theme accent palette, one
// token per morph. Names only — the actual colours stay design tokens, resolved
// via var() so the loader tracks any palette change. CSS eases the fill (see
// .c-loader--color .c-loader__path), so JS just hands over the next token.
const COLORS = ['--c-blue-400', '--c-purple-400', '--c-magenta-400', '--c-teal-400', '--c-coral-400'];

// A beat of stillness held between morphs, so each transition reads as a distinct
// "morph → hold → morph". Uses the `short` duration token (160ms), read live so it
// stays token-driven. (Moot under reduced motion — the loop doesn't run there.)
const HOLD_TOKEN = '--motion-duration-short';
const holdMs = () => {
  const v = getComputedStyle(document.documentElement).getPropertyValue(HOLD_TOKEN).trim();
  return parseFloat(v) * (v.endsWith('ms') ? 1 : 1000) || 0;
};

document.addEventListener('alpine:init', () => {
  // Morphing brand loader. Tweens one brand shape into the next on the `extra-long
  // emphasized` fragment, holds briefly, and loops forever. Visibility can be bound
  // to a global loader-store key; the loop only runs while the mark is on screen
  // AND (keyless, or its store key is active), and never under reduced motion.
  //
  // The animation machinery lives in this per-instance closure rather than in the
  // returned reactive object, so the shared geometry rings and the observer stay
  // raw (no per-frame proxy overhead). Only `sync` is exposed — its store read is
  // the single reactive dependency x-effect needs to react to show()/hide().
  Alpine.data('loaderMorph', () => {
    let geo = null;
    let io = null;
    let cancel = null;
    let store = null;
    let pathEl = null;
    let key = '';
    let index = 0;
    let running = false;
    let reduce = false;
    let onscreen = false;
    let colorful = false;
    let colorIndex = 0;
    let pauseTimer = null;

    // Cancel whatever the loop has pending — the active tween leg or the between-
    // shapes hold — so stop()/teardown leave nothing running.
    const clearPending = () => {
      if (cancel) { cancel(); cancel = null; }
      if (pauseTimer) { clearTimeout(pauseTimer); pauseTimer = null; }
    };

    // One morph leg: current ring → next, then hold, then recurse to loop forever.
    // tween() drives the morph on a rAF clock and the hold is a timeout, so this
    // is scheduled, not deep recursion.
    const leg = () => {
      if (!running) return;
      // Colour variant: hand the mark its next accent as the shape starts moving;
      // the CSS fill transition crossfades it over the same beat as the morph.
      if (colorful) {
        colorIndex = (colorIndex + 1) % COLORS.length;
        pathEl.style.fill = `var(${COLORS[colorIndex]})`;
      }
      const from = geo[index];
      const to = geo[(index + 1) % geo.length];
      cancel = tween(
        MOTION,
        (t) => pathEl.setAttribute('d', buildPath(lerpPoints(from, to, t))),
        () => {
          cancel = null;
          index = (index + 1) % geo.length;
          // Rest on the finished shape before the next morph — the punctuating
          // hold. leg() re-checks `running`, so a stop() during the pause is safe.
          pauseTimer = setTimeout(() => { pauseTimer = null; leg(); }, holdMs());
        },
      );
    };

    const start = () => {
      if (running || reduce) return;
      running = true;
      leg();
    };

    const stop = () => {
      running = false;
      clearPending();
    };

    const shouldRun = () => {
      // Read the store FIRST and unconditionally: x-effect only subscribes to a
      // reactive value it actually reads during a run. Short-circuiting on the
      // hidden first run (onscreen still false) would skip this read, so the
      // effect would never re-fire on show()/hide() and the morph never starts.
      const active = key ? store.isActive(key) : true;
      return active && !reduce && !!geo && onscreen;
    };

    return {
      init() {
        key = this.$root.dataset.loaderKey || '';
        store = this.$store.loader;
        pathEl = this.$refs.path;
        reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        geo = getLoaderGeometry();

        // Seed the resting shape so the mark is correct before the first frame —
        // and this is the final rendered state under reduced motion (no loop).
        pathEl.setAttribute('d', buildPath(geo[0]));

        // Colour variant: seed the first accent so the mark opens on a palette
        // colour (and rests on one under reduced motion). leg() advances from here.
        colorful = this.$root.classList.contains('c-loader--color');
        if (colorful) pathEl.style.fill = `var(${COLORS[0]})`;

        // Pause whenever the mark isn't on screen: display:none hosts, scrolled-
        // or tabbed-away. Native + cheap, so no rAF burns while it's hidden.
        io = new IntersectionObserver(([entry]) => {
          onscreen = entry.isIntersecting;
          this.sync();
        });
        io.observe(this.$root);
      },

      destroy() {
        io?.disconnect();
        stop();
      },

      // Re-evaluated reactively via x-effect (store key) and called by the
      // observer (visibility). Reading the store in shouldRun() registers the
      // reactive dependency that makes x-effect fire on show()/hide().
      sync() {
        shouldRun() ? start() : stop();
      },
    };
  });
});
